import React, { useState, useEffect } from "react";
import { useReactFlow } from "reactflow";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import { useAppDispatch } from "../../hooks";
import {
  loadDesignState,
  resetGame,
  setIopsConfigModalOpen,
  selectBlockForTour,
  setPaletteCollapsed,
} from "../../store/game/gameSlice";
import {
  COLOR_PRIMARY,
  COLOR_PRIMARY_DARK,
  COLOR_SECONDARY,
} from "../../styles/colors";

// Bounding Rect tracking hook
const useElementRect = (selector: string | null, active: boolean, step: number) => {
  const [rect, setRect] = useState<DOMRect | null>(null);

  useEffect(() => {
    if (!active || !selector) {
      setRect(null);
      return;
    }

    const updateRect = () => {
      const el = document.querySelector(selector);
      if (el) {
        setRect(el.getBoundingClientRect());
      } else {
        setRect(null);
      }
    };

    updateRect();
    const interval = setInterval(updateRect, 150);
    window.addEventListener("resize", updateRect);

    return () => {
      clearInterval(interval);
      window.removeEventListener("resize", updateRect);
    };
  }, [selector, active, step]);

  return rect;
};

// Spotlight Overlay using an SVG mask
const SpotlightOverlay: React.FC<{ rect: DOMRect | null }> = ({ rect }) => {
  if (!rect) return null;

  return (
    <svg
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        zIndex: 9999,
        pointerEvents: "none",
      }}
    >
      <defs>
        <mask id="spotlight-mask">
          <rect width="100%" height="100%" fill="white" />
          <rect
            x={rect.left - 12}
            y={rect.top - 12}
            width={rect.width + 24}
            height={rect.height + 24}
            rx={10}
            fill="black"
          />
        </mask>
      </defs>
      <rect
        width="100%"
        height="100%"
        fill="rgba(15, 23, 42, 0.6)"
        mask="url(#spotlight-mask)"
        style={{ pointerEvents: "auto" }}
      />
    </svg>
  );
};

interface StepConfig {
  selector: string | null;
  title: string;
  description: string;
  placement: "right" | "bottom" | "bottom-right" | "center";
}

const TOUR_STEPS: StepConfig[] = [
  {
    selector: "#palette-container",
    title: "Block Palette",
    description: "This is the Block Palette. You can click on any block to place it on the canvas, or drag and drop blocks directly to build your system design architecture.",
    placement: "right",
  },
  {
    selector: '[data-testid="block-load_balancer"]',
    title: "Block Details & Info",
    description: "We've selected the Application Load Balancer. Notice the Block Info panel showing current TPS vs Capacity. You can use the floating action buttons on the left of the block to edit its name, open settings, view info, or delete the block.",
    placement: "bottom",
  },
  {
    selector: "#scoreboard-iops-display",
    title: "Simulation Traffic Metrics",
    description: "This is the IOPS display on the Scoreboard. It shows the overall TPS entering your system currently, and you can click on it to adjust the simulation traffic settings.",
    placement: "bottom",
  },
  {
    selector: ".MuiDialog-paper",
    title: "Simulation Settings",
    description: "This is the Traffic Config modal. Here, you can adjust the base TPS, choose different traffic patterns (steady, waves, or ramping load), and customize read/write routing configurations.",
    placement: "center",
  },
  {
    selector: "#scoreboard-actions-container",
    title: "Controls & Actions",
    description: "These are the control actions. Start/pause the simulation, clear the canvas for a new design, save your designs, or reset the metrics. Click 'New Design' after the tour to start your own scratchpad!",
    placement: "bottom-right",
  },
];

const INTRO_GAME_STATE = {
  nodes: [
    {
      id: "root",
      type: "gameBlock",
      data: {
        label: "User",
        blockType: "user",
      },
      position: {
        x: 444.11651689888373,
        y: 208.96440450636896,
      },
      width: 187,
      height: 100,
      selected: false,
      positionAbsolute: {
        x: 444.11651689888373,
        y: 208.96440450636896,
      },
      dragging: false,
    },
    {
      id: "7b8ecbb11d2c5",
      position: {
        x: 666.4143374442542,
        y: 788.2257780262025,
      },
      type: "gameBlock",
      data: {
        label: "Database 1",
        blockType: "database",
      },
      width: 185,
      height: 100,
      selected: false,
      dragging: false,
      positionAbsolute: {
        x: 666.4143374442542,
        y: 788.2257780262025,
      },
    },
    {
      id: "ha8dg8jae225",
      position: {
        x: 438.91340173385174,
        y: 413.8943250290031,
      },
      type: "gameBlock",
      data: {
        label: "Application Load Balancer 1",
        blockType: "load_balancer",
      },
      width: 159,
      height: 100,
      selected: false,
      positionAbsolute: {
        x: 438.91340173385174,
        y: 413.8943250290031,
      },
      dragging: false,
    },
    {
      id: "4j9442ii3h81e",
      position: {
        x: 275.2498958341805,
        y: 774.3022582335984,
      },
      type: "gameBlock",
      data: {
        label: "Database 2",
        blockType: "database",
      },
      width: 159,
      height: 100,
      selected: false,
      positionAbsolute: {
        x: 275.2498958341805,
        y: 774.3022582335984,
      },
      dragging: false,
    },
    {
      id: "a6ecg0cd98j6a",
      position: {
        x: 275.24989583418045,
        y: 598.4510444477813,
      },
      type: "gameBlock",
      data: {
        label: "App Server 1",
        blockType: "app_server",
      },
      width: 159,
      height: 100,
    },
    {
      id: "h873b9afdfc3e",
      position: {
        x: 666.9976493174362,
        y: 594.9688421945968,
      },
      type: "gameBlock",
      data: {
        label: "App Server 2",
        blockType: "app_server",
      },
      width: 159,
      height: 100,
      selected: false,
      positionAbsolute: {
        x: 666.9976493174362,
        y: 594.9688421945968,
      },
      dragging: false,
    },
  ],
  edges: [
    {
      style: {
        strokeWidth: 3,
        stroke: "#6b7280",
      },
      source: "root",
      sourceHandle: "b",
      target: "ha8dg8jae225",
      targetHandle: null,
      type: "gameEdge",
      id: "reactflow__edge-rootb-ha8dg8jae225",
    },
    {
      style: {
        strokeWidth: 3,
        stroke: "#6b7280",
      },
      source: "ha8dg8jae225",
      sourceHandle: "b",
      target: "a6ecg0cd98j6a",
      targetHandle: null,
      type: "gameEdge",
      id: "reactflow__edge-ha8dg8jae225b-a6ecg0cd98j6a",
    },
    {
      style: {
        strokeWidth: 3,
        stroke: "#6b7280",
      },
      source: "a6ecg0cd98j6a",
      sourceHandle: "b",
      target: "4j9442ii3h81e",
      targetHandle: null,
      type: "gameEdge",
      id: "reactflow__edge-a6ecg0cd98j6ab-4j9442ii3h81e",
    },
    {
      style: {
        strokeWidth: 3,
        stroke: "#6b7280",
      },
      source: "ha8dg8jae225",
      sourceHandle: "b",
      target: "h873b9afdfc3e",
      targetHandle: null,
      type: "gameEdge",
      id: "reactflow__edge-ha8dg8jae225b-h873b9afdfc3e",
    },
    {
      style: {
        strokeWidth: 3,
        stroke: "#6b7280",
      },
      source: "h873b9afdfc3e",
      sourceHandle: "b",
      target: "7b8ecbb11d2c5",
      targetHandle: null,
      type: "gameEdge",
      id: "reactflow__edge-h873b9afdfc3eb-7b8ecbb11d2c5",
    },
  ],
  blocks: {
    root: {
      top: 208.96440450636896,
      left: 444.11651689888373,
      id: "root",
      width: 100,
      height: 100,
      blockType: "user",
      inputCapacity: {
        total: 0,
        byOperationType: {
          read: {
            total: 0,
            byContentType: {
              dynamic: 0,
              static: 0,
              static_media: 0,
            },
          },
          write: {
            total: 0,
            byContentType: {
              dynamic: 0,
              static: 0,
              static_media: 0,
            },
          },
        },
      },
      processingCapacity: {
        total: 0,
        byOperationType: {
          read: {
            total: 0,
            byContentType: {
              dynamic: 0,
              static: 0,
              static_media: 0,
            },
          },
          write: {
            total: 0,
            byContentType: {
              dynamic: 0,
              static: 0,
              static_media: 0,
            },
          },
        },
      },
    },
    "7b8ecbb11d2c5": {
      top: 788.2257780262025,
      left: 666.4143374442542,
      id: "7b8ecbb11d2c5",
      width: 100,
      height: 100,
      blockType: "database",
      inputCapacity: {
        total: 3000,
        byOperationType: {
          read: {
            total: 2250,
            byContentType: {
              dynamic: 500,
              static: 1000,
              static_media: 750,
            },
          },
          write: {
            total: 750,
            byContentType: {
              dynamic: 500,
              static_media: 250,
            },
          },
        },
      },
      processingCapacity: {
        total: 3000,
        byOperationType: {
          read: {
            total: 2250,
            byContentType: {
              dynamic: 500,
              static: 1000,
              static_media: 750,
            },
          },
          write: {
            total: 750,
            byContentType: {
              dynamic: 500,
              static_media: 250,
            },
          },
        },
      },
      customName: "Database 1",
    },
    ha8dg8jae225: {
      top: 413.8943250290031,
      left: 438.91340173385174,
      id: "ha8dg8jae225",
      width: 100,
      height: 100,
      blockType: "load_balancer",
      inputCapacity: {
        total: 150000,
        byOperationType: {
          read: {
            total: 90000,
            byContentType: {
              dynamic: 30000,
              static: 30000,
              static_media: 30000,
            },
          },
          write: {
            total: 60000,
            byContentType: {
              dynamic: 40000,
              static_media: 20000,
            },
          },
        },
      },
      processingCapacity: {
        total: 0,
        byOperationType: {
          read: {
            total: 0,
            byContentType: {
              dynamic: 0,
              static: 0,
              static_media: 0,
            },
          },
          write: {
            total: 0,
            byContentType: {
              dynamic: 0,
              static: 0,
              static_media: 0,
            },
          },
        },
      },
      customName: "Application Load Balancer 1",
    },
    "4j9442ii3h81e": {
      top: 774.3022582335984,
      left: 275.2498958341805,
      id: "4j9442ii3h81e",
      width: 100,
      height: 100,
      blockType: "database",
      inputCapacity: {
        total: 3000,
        byOperationType: {
          read: {
            total: 2250,
            byContentType: {
              dynamic: 500,
              static: 1000,
              static_media: 750,
            },
          },
          write: {
            total: 750,
            byContentType: {
              dynamic: 500,
              static_media: 250,
            },
          },
        },
      },
      processingCapacity: {
        total: 3000,
        byOperationType: {
          read: {
            total: 2250,
            byContentType: {
              dynamic: 500,
              static: 1000,
              static_media: 750,
            },
          },
          write: {
            total: 750,
            byContentType: {
              dynamic: 500,
              static_media: 250,
            },
          },
        },
      },
      customName: "Database 2",
    },
    a6ecg0cd98j6a: {
      top: 598.4510444477813,
      left: 275.24989583418045,
      id: "a6ecg0cd98j6a",
      width: 100,
      height: 100,
      blockType: "app_server",
      inputCapacity: {
        total: 1500,
        byOperationType: {
          read: {
            total: 900,
            byContentType: {
              dynamic: 300,
              static: 300,
              static_media: 300,
            },
          },
          write: {
            total: 600,
            byContentType: {
              dynamic: 400,
              static_media: 200,
            },
          },
        },
      },
      processingCapacity: {
        total: 0,
        byOperationType: {
          read: {
            total: 0,
            byContentType: {
              dynamic: 0,
              static: 0,
              static_media: 0,
            },
          },
          write: {
            total: 0,
            byContentType: {
              dynamic: 0,
              static: 0,
              static_media: 0,
            },
          },
        },
      },
      customName: "App Server 1",
    },
    h873b9afdfc3e: {
      top: 594.9688421945968,
      left: 666.9976493174362,
      id: "h873b9afdfc3e",
      width: 100,
      height: 100,
      blockType: "app_server",
      inputCapacity: {
        total: 1500,
        byOperationType: {
          read: {
            total: 900,
            byContentType: {
              dynamic: 300,
              static: 300,
              static_media: 300,
            },
          },
          write: {
            total: 600,
            byContentType: {
              dynamic: 400,
              static_media: 200,
            },
          },
        },
      },
      processingCapacity: {
        total: 0,
        byOperationType: {
          read: {
            total: 0,
            byContentType: {
              dynamic: 0,
              static: 0,
              static_media: 0,
            },
          },
          write: {
            total: 0,
            byContentType: {
              dynamic: 0,
              static: 0,
              static_media: 0,
            },
          },
        },
      },
      customName: "App Server 2",
    },
  },
  connectors: {},
  blockIOPS: {
    root: {
      blockID: "root",
      inputIOPS: {
        total: 1000,
        byOperationType: {
          read: {
            total: 750,
            byContentType: {
              dynamic: 250,
              static: 500,
              static_media: 0,
            },
          },
          write: {
            total: 250,
            byContentType: {
              dynamic: 250,
              static: 0,
              static_media: 0,
            },
          },
        },
      },
      outputIOPS: {
        total: 1000,
        byOperationType: {
          read: {
            total: 750,
            byContentType: {
              dynamic: 250,
              static: 500,
              static_media: 0,
            },
          },
          write: {
            total: 250,
            byContentType: {
              dynamic: 250,
              static: 0,
              static_media: 0,
            },
          },
        },
      },
      percentageSuccess: 80,
    },
    "7b8ecbb11d2c5": {
      blockID: "7b8ecbb11d2c5",
      inputIOPS: {
        total: 800,
        byOperationType: {
          read: {
            total: 550,
            byContentType: {
              dynamic: 250,
              static: 300,
              static_media: 0,
            },
          },
          write: {
            total: 250,
            byContentType: {
              dynamic: 250,
              static: 0,
              static_media: 0,
            },
          },
        },
      },
      outputIOPS: {
        total: 0,
        byOperationType: {
          read: {
            total: 0,
            byContentType: {
              dynamic: 0,
              static: 0,
              static_media: 0,
            },
          },
          write: {
            total: 0,
            byContentType: {
              dynamic: 0,
              static: 0,
              static_media: 0,
            },
          },
        },
      },
      percentageSuccess: 100,
    },
    ha8dg8jae225: {
      blockID: "ha8dg8jae225",
      inputIOPS: {
        total: 0,
        byOperationType: {
          read: {
            total: 0,
            byContentType: {
              dynamic: 0,
              static: 0,
              static_media: 0,
            },
          },
          write: {
            total: 0,
            byContentType: {
              dynamic: 0,
              static: 0,
              static_media: 0,
            },
          },
        },
      },
      outputIOPS: {
        total: 0,
        byOperationType: {
          read: {
            total: 0,
            byContentType: {
              dynamic: 0,
              static: 0,
              static_media: 0,
            },
          },
          write: {
            total: 0,
            byContentType: {
              dynamic: 0,
              static: 0,
              static_media: 0,
            },
          },
        },
      },
      percentageSuccess: 100,
    },
    "4j9442ii3h81e": {
      blockID: "4j9442ii3h81e",
      inputIOPS: {
        total: 0,
        byOperationType: {
          read: {
            total: 0,
            byContentType: {
              dynamic: 0,
              static: 0,
              static_media: 0,
            },
          },
          write: {
            total: 0,
            byContentType: {
              dynamic: 0,
              static: 0,
              static_media: 0,
            },
          },
        },
      },
      outputIOPS: {
        total: 0,
        byOperationType: {
          read: {
            total: 0,
            byContentType: {
              dynamic: 0,
              static: 0,
              static_media: 0,
            },
          },
          write: {
            total: 0,
            byContentType: {
              dynamic: 0,
              static: 0,
              static_media: 0,
            },
          },
        },
      },
      percentageSuccess: 100,
    },
    a6ecg0cd98j6a: {
      blockID: "a6ecg0cd98j6a",
      inputIOPS: {
        total: 0,
        byOperationType: {
          read: {
            total: 0,
            byContentType: {
              dynamic: 0,
              static: 0,
              static_media: 0,
            },
          },
          write: {
            total: 0,
            byContentType: {
              dynamic: 0,
              static: 0,
              static_media: 0,
            },
          },
        },
      },
      outputIOPS: {
        total: 0,
        byOperationType: {
          read: {
            total: 0,
            byContentType: {
              dynamic: 0,
              static: 0,
              static_media: 0,
            },
          },
          write: {
            total: 0,
            byContentType: {
              dynamic: 0,
              static: 0,
              static_media: 0,
            },
          },
        },
      },
      percentageSuccess: 100,
    },
    h873b9afdfc3e: {
      blockID: "h873b9afdfc3e",
      inputIOPS: {
        total: 0,
        byOperationType: {
          read: {
            total: 0,
            byContentType: {
              dynamic: 0,
              static: 0,
              static_media: 0,
            },
          },
          write: {
            total: 0,
            byContentType: {
              dynamic: 0,
              static: 0,
              static_media: 0,
            },
          },
        },
      },
      outputIOPS: {
        total: 0,
        byOperationType: {
          read: {
            total: 0,
            byContentType: {
              dynamic: 0,
              static: 0,
              static_media: 0,
            },
          },
          write: {
            total: 0,
            byContentType: {
              dynamic: 0,
              static: 0,
              static_media: 0,
            },
          },
        },
      },
      percentageSuccess: 100,
    },
  },
  timeElapsedInSec: 80,
  level: 3,
  isRunning: false,
  isStarted: true,
  isOver: false,
  connectionStartBlock: null,
  connectionStartNode: null,
  selectedBlock: null,
  draggingBlock: null,
  accountBalance: -491.59999999999945,
  initialRootIOPS: {
    total: 1000,
    byOperationType: {
      read: {
        total: 750,
        byContentType: {
          dynamic: 250,
          static: 500,
          static_media: 0,
        },
      },
      write: {
        total: 250,
        byContentType: {
          dynamic: 250,
          static: 0,
          static_media: 0,
        },
      },
    },
  },
  trafficPattern: "STEADY",
  trafficRoutes: [
    {
      id: "default-app-route",
      path: "/app",
      weight: 100,
      contentTypes: [
        {
          id: "default-static",
          type: "static",
          weight: 50,
          readRatio: 100,
        },
        {
          id: "default-dynamic",
          type: "dynamic",
          weight: 50,
          readRatio: 50,
        },
      ],
    },
  ],
  settingsModalOpenForBlockId: null,
  isDraggingNode: false,
  minTPS: 1000,
  maxTPS: 1000,
};

export const IntroTour: React.FC = () => {
  const dispatch = useAppDispatch();
  const { setViewport, fitView } = useReactFlow();

  const [welcomeOpen, setWelcomeOpen] = useState(false);
  const [tourActive, setTourActive] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);

  // Check LocalStorage flag before prompting
  useEffect(() => {
    const completed = localStorage.getItem("intro_completed");
    if (completed !== "true") {
      setWelcomeOpen(true);
    }
  }, []);

  const activeStep = TOUR_STEPS[stepIndex];
  const rawTargetRect = useElementRect(activeStep?.selector || null, tourActive, stepIndex);

  const getAdjustedRect = (step: number, rect: DOMRect | null) => {
    if (!rect) return null;
    if (step !== 1) return rect;

    const blockEl = document.querySelector('[data-testid="block-load_balancer"]');
    if (!blockEl) return rect;

    const nodeWrapper = blockEl.closest(".react-flow__node");
    if (!nodeWrapper) return rect;

    let minLeft = rect.left;
    let maxRight = rect.right;
    let minTop = rect.top;
    let maxBottom = rect.bottom;

    // Union with the node wrapper's bounding box
    const wrapperRect = nodeWrapper.getBoundingClientRect();
    minLeft = Math.min(minLeft, wrapperRect.left);
    maxRight = Math.max(maxRight, wrapperRect.right);
    minTop = Math.min(minTop, wrapperRect.top);
    maxBottom = Math.max(maxBottom, wrapperRect.bottom);

    // Union with all MuiCard components (block card + BlockInfoDisplay)
    const cards = nodeWrapper.querySelectorAll(".MuiCard-root");
    cards.forEach((card) => {
      const cardRect = card.getBoundingClientRect();
      minLeft = Math.min(minLeft, cardRect.left);
      maxRight = Math.max(maxRight, cardRect.right);
      minTop = Math.min(minTop, cardRect.top);
      maxBottom = Math.max(maxBottom, cardRect.bottom);
    });

    // Union with all Floating Action Buttons in the toolbar
    const fabs = nodeWrapper.querySelectorAll(".MuiFab-root");
    fabs.forEach((fab) => {
      const fabRect = fab.getBoundingClientRect();
      minLeft = Math.min(minLeft, fabRect.left);
      maxRight = Math.max(maxRight, fabRect.right);
      minTop = Math.min(minTop, fabRect.top);
      maxBottom = Math.max(maxBottom, fabRect.bottom);
    });

    // Union with the NodeToolbar element if available
    const toolbar = nodeWrapper.querySelector(".react-flow__node-toolbar");
    if (toolbar) {
      const toolbarRect = toolbar.getBoundingClientRect();
      minLeft = Math.min(minLeft, toolbarRect.left);
      maxRight = Math.max(maxRight, toolbarRect.right);
      minTop = Math.min(minTop, toolbarRect.top);
      maxBottom = Math.max(maxBottom, toolbarRect.bottom);
    }

    return {
      left: minLeft - 50,
      right: maxRight,
      top: minTop,
      bottom: maxBottom,
      width: maxRight - (minLeft - 50),
      height: maxBottom - minTop,
    } as DOMRect;
  };

  const targetRect = getAdjustedRect(stepIndex, rawTargetRect);

  const startTour = () => {
    setWelcomeOpen(false);
    // Load introductory game state
    dispatch(loadDesignState(INTRO_GAME_STATE as any));
    setTourActive(true);
    setStepIndex(0);
    // Center viewport appropriately
    setViewport({ x: 0, y: 0, zoom: 0.85 });
  };

  const handleSkip = () => {
    localStorage.setItem("intro_completed", "true");
    setWelcomeOpen(false);
    setTourActive(false);
    dispatch(resetGame());
    dispatch(setPaletteCollapsed(false));
    setViewport({ x: 0, y: 0, zoom: 1 });
  };

  const handleNext = () => {
    const nextIdx = stepIndex + 1;
    if (nextIdx < TOUR_STEPS.length) {
      setStepIndex(nextIdx);
    } else {
      handleFinish();
    }
  };

  const handleBack = () => {
    if (stepIndex > 0) {
      setStepIndex(stepIndex - 1);
    }
  };

  const handleFinish = () => {
    localStorage.setItem("intro_completed", "true");
    setTourActive(false);
    dispatch(resetGame());
    dispatch(setPaletteCollapsed(false));
    setViewport({ x: 0, y: 0, zoom: 1 });
  };

  // Step state-trigger side effects
  useEffect(() => {
    if (!tourActive) return;

    // Collapse or expand palette
    if (stepIndex === 0) {
      dispatch(setPaletteCollapsed(false));
    } else {
      dispatch(setPaletteCollapsed(true));
    }

    // Step 2 is Load Balancer block details
    if (stepIndex === 1) {
      // Programmatically select load balancer block
      dispatch(selectBlockForTour("ha8dg8jae225"));
      // Center the viewport so the entire design is visible
      // Wait 150ms to allow palette collapse transition to complete
      setTimeout(() => {
        fitView({ duration: 800 });
      }, 150);
    } else {
      // Clear block selection
      dispatch(selectBlockForTour(null));
    }

    // Step 4 is IOPS Config modal
    if (stepIndex === 3) {
      dispatch(setIopsConfigModalOpen(true));
    } else {
      dispatch(setIopsConfigModalOpen(false));
    }
  }, [stepIndex, tourActive, dispatch, fitView]);

  if (welcomeOpen) {
    return (
      <Dialog
        open={welcomeOpen}
        onClose={handleSkip}
        maxWidth="xs"
        fullWidth
        sx={{
          "& .MuiDialog-paper": {
            borderRadius: 2,
            padding: 2,
            boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
          },
        }}
      >
        <DialogTitle sx={{ textAlign: "center", pb: 1 }}>
          <Typography variant="h5" fontWeight="800" color="primary.dark">
            🚀 Welcome!
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ textAlign: "center", pb: 2 }}>
          <Typography variant="body1" color="textPrimary" sx={{ mb: 2 }}>
            Welcome to the <strong>System Design Playground</strong>! Learn how to design, simulate, and scale modern web architectures.
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Would you like a quick 1-minute guided tour of the core features?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", gap: 1, px: 3, pb: 2 }}>
          <Button
            onClick={handleSkip}
            variant="outlined"
            sx={{
              borderRadius: "20px",
              px: 3,
              borderColor: COLOR_PRIMARY,
              color: COLOR_PRIMARY,
              fontWeight: 600,
              textTransform: "none",
              "&:hover": { borderColor: COLOR_PRIMARY_DARK },
            }}
          >
            Skip
          </Button>
          <Button
            onClick={startTour}
            variant="contained"
            sx={{
              borderRadius: "20px",
              px: 3,
              backgroundColor: COLOR_PRIMARY,
              color: "white",
              fontWeight: 700,
              textTransform: "none",
              "&:hover": { backgroundColor: COLOR_PRIMARY_DARK },
            }}
          >
            Start Tour
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  if (!tourActive || !activeStep) return null;

  // Compute popover positions
  let top = window.innerHeight / 2 - 100;
  let left = window.innerWidth / 2 - 175;
  const tooltipWidth = 350;
  const tooltipHeight = 200;

  if (targetRect) {
    if (activeStep.placement === "right") {
      left = targetRect.right + 20;
      top = targetRect.top + targetRect.height / 2 - tooltipHeight / 2;
    } else if (activeStep.placement === "bottom") {
      left = targetRect.left + targetRect.width / 2 - tooltipWidth / 2;
      top = targetRect.bottom + 20;
    } else if (activeStep.placement === "bottom-right") {
      left = targetRect.right - tooltipWidth;
      top = targetRect.bottom + 20;
    } else if (activeStep.placement === "center") {
      left = window.innerWidth / 2 - tooltipWidth / 2;
      top = window.innerHeight / 2 - tooltipHeight / 2;
    }
  }

  // Clamp within viewport
  const finalLeft = Math.max(16, Math.min(window.innerWidth - tooltipWidth - 16, left));
  const finalTop = Math.max(16, Math.min(window.innerHeight - tooltipHeight - 16, top));

  return (
    <>
      <SpotlightOverlay rect={targetRect} />
      <Card
        elevation={12}
        sx={{
          position: "fixed",
          top: finalTop,
          left: finalLeft,
          width: `${tooltipWidth}px`,
          zIndex: 10000,
          background: "rgba(15, 23, 42, 0.95)",
          backdropFilter: "blur(8px)",
          border: "1px solid rgba(255, 255, 255, 0.15)",
          borderRadius: 2,
          padding: "20px",
          color: "#f8fafc",
          boxSizing: "border-box",
          transition: "top 0.2s ease-out, left 0.2s ease-out",
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="subtitle1" fontWeight="800" sx={{ color: COLOR_SECONDARY }}>
              {activeStep.title}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.6, fontWeight: 600 }}>
              Step {stepIndex + 1} of {TOUR_STEPS.length}
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ lineHeight: 1.5, opacity: 0.9 }}>
            {activeStep.description}
          </Typography>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mt: 1,
            }}
          >
            <Button
              size="small"
              onClick={handleSkip}
              sx={{
                color: "#94a3b8",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "12px",
                minWidth: 0,
                padding: 0,
                "&:hover": { color: "#cbd5e1" },
              }}
            >
              Skip Tour
            </Button>
            <Box sx={{ display: "flex", gap: 1 }}>
              {stepIndex > 0 && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleBack}
                  sx={{
                    borderRadius: "15px",
                    color: "#f8fafc",
                    borderColor: "rgba(255,255,255,0.3)",
                    textTransform: "none",
                    fontWeight: 600,
                    px: 2,
                    "&:hover": {
                      borderColor: "#f8fafc",
                      backgroundColor: "rgba(255,255,255,0.05)",
                    },
                  }}
                >
                  Back
                </Button>
              )}
              <Button
                size="small"
                variant="contained"
                onClick={handleNext}
                sx={{
                  borderRadius: "15px",
                  backgroundColor: COLOR_PRIMARY,
                  color: "white",
                  fontWeight: 700,
                  textTransform: "none",
                  px: 2.5,
                  "&:hover": { backgroundColor: COLOR_PRIMARY_DARK },
                }}
              >
                {stepIndex === TOUR_STEPS.length - 1 ? "Finish" : "Next"}
              </Button>
            </Box>
          </Box>
        </Box>
      </Card>
    </>
  );
};
